import { FC, useState } from 'react';
import { IonButton, IonList, IonItem, IonSearchbar } from '@ionic/react';
import { createUseStyles } from 'react-jss';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { useSubscription } from '@meraki-internal/state';
import { BeeChatState } from './BeeChatState';
import { IChat } from './BeeChatClient';
import { ChevronRightIcon, CommentPlusIcon } from '../theme/Icons';
import dayjs from 'dayjs';

const useStyles = createUseStyles({
    container: {
        display: 'flex',
        flexDirection: 'column',
        height: '100%'
    },

    searchContainer: {
        padding: '8px 16px',
        borderBottom: '1px solid var(--ion-color-step-100)'
    },

    searchbar: {
        '--background': 'var(--ion-color-step-50)',
        '--border-radius': '20px',
        '--box-shadow': 'none',
        '--placeholder-color': 'var(--ion-color-medium)',
        '--icon-color': 'var(--ion-color-medium)',
        padding: 0,
        minHeight: 36,

        '& .searchbar-search-icon': {
            marginInlineStart: '8px !important'
        },

        '& .searchbar-input': {
            paddingInlineStart: '42px !important'
        }
    },

    listContainer: {
        flex: 1,
        overflowY: 'auto'
    },

    conversationItem: {
        '--padding-start': '16px',
        '--padding-end': '16px',
        '--inner-padding-end': '0',
        cursor: 'pointer'
    },

    itemContent: {
        display: 'flex',
        alignItems: 'center',
        width: '100%',
        padding: '12px 0',
        gap: 12
    },

    summaryContainer: {
        flex: 1,
        minWidth: 0
    },

    summary: {
        fontSize: 15,
        color: 'var(--ion-color-dark)',
        margin: 0,
        lineHeight: 1.4,
        display: '-webkit-box',
        WebkitLineClamp: 2,
        WebkitBoxOrient: 'vertical',
        overflow: 'hidden',
        textOverflow: 'ellipsis'
    },

    timestamp: {
        fontSize: 13,
        color: 'var(--ion-color-medium)',
        flexShrink: 0,
        marginLeft: 8
    },

    chevronWrapper: {
        display: 'flex',
        alignItems: 'center',
        flexShrink: 0
    },

    buttonContainer: {
        padding: 6,
        borderTop: '1px solid var(--ion-color-step-100)',
        display: 'flex',
        justifyContent: 'center',
        marginBottom: 'var(--ion-safe-area-bottom, 0px)'
    },

    emptyState: {
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 32,
        textAlign: 'center'
    },

    emptyStateText: {
        fontSize: 16,
        color: 'var(--ion-color-medium)',
        marginBottom: 24
    },

    noResults: {
        padding: 32,
        textAlign: 'center',
        color: 'var(--ion-color-medium)',
        fontSize: 15
    }
});

interface ConversationListViewProps {
    'data-id'?: string;
}

export const ConversationListView: FC<ConversationListViewProps> = ({ 'data-id': dataId }) => {
    const classes = useStyles();

    const chatState = useInstance(BeeChatState);
    useSubscription(() => chatState);

    const { chats } = chatState.state;
    const [searchQuery, setSearchQuery] = useState('');

    const filterChats = (allChats: IChat[], query: string): IChat[] => {
        if (!query.trim()) {
            return allChats;
        }
        const normalizedQuery = query.toLowerCase();
        return allChats.filter(chat =>
            chat.messages.some(msg =>
                msg.content.toLowerCase().includes(normalizedQuery)
            )
        );
    };

    const filteredChats = filterChats(chats, searchQuery);

    const formatTimestamp = (lastMessageAt: string): string => {
        const date = dayjs(lastMessageAt);
        const now = dayjs();

        if (date.isSame(now, 'day')) {
            return date.format('h:mm A');
        }
        if (date.isSame(now.subtract(1, 'day'), 'day')) {
            return 'Yesterday';
        }
        if (date.isSame(now, 'year')) {
            return date.format('MMM D');
        }
        return date.format('MMM D, YYYY');
    };

    const handleSelectChat = (chatId: string) => {
        chatState.selectChat(chatId);
    };

    const handleStartNewChat = () => {
        chatState.startNewConversation();
    };

    const renderChatRow = (chat: IChat) => {
        return (
            <IonItem
                key={chat.chatId}
                className={classes.conversationItem}
                lines="full"
                button
                detail={false}
                onClick={() => handleSelectChat(chat.chatId)}
                data-id={`bea-conversation-row-${chat.chatId}`}
            >
                <div className={classes.itemContent}>
                    <div className={classes.summaryContainer}>
                        <p className={classes.summary}>
                            {chatState.getSummary(chat)}
                        </p>
                    </div>
                    <span className={classes.timestamp}>
                        {formatTimestamp(chat.lastMessageAt)}
                    </span>
                    <div className={classes.chevronWrapper}>
                        <ChevronRightIcon color="medium" size="small" />
                    </div>
                </div>
            </IonItem>
        );
    };

    return (
        <div className={classes.container} data-id={dataId || 'bea-conversation-list'}>
            {chats.length > 0 && (
                <div className={classes.searchContainer}>
                    <IonSearchbar
                        className={classes.searchbar}
                        placeholder="Search..."
                        value={searchQuery}
                        onIonInput={(e) => setSearchQuery(e.detail.value || '')}
                        debounce={0}
                        mode="ios"
                        data-id="bea-conversation-search"
                    />
                </div>
            )}

            {chats.length > 0 ? (
                filteredChats.length > 0 ? (
                    <div className={classes.listContainer}>
                        <IonList>
                            {filteredChats.map(chat => renderChatRow(chat))}
                        </IonList>
                    </div>
                ) : (
                    <div className={classes.noResults}>
                        No conversations match your search
                    </div>
                )
            ) : (
                <div className={classes.emptyState}>
                    <p className={classes.emptyStateText}>
                        No conversations yet. Start a new chat with Bea!
                    </p>
                </div>
            )}

            <div className={classes.buttonContainer}>
                <IonButton
                    color="primary"
                    onClick={handleStartNewChat}
                    data-id="bea-start-new-chat-button"
                >
                    <CommentPlusIcon color="light" size="large" />
                    &nbsp;&nbsp;Start a New Chat
                </IonButton>
            </div>
        </div>
    );
};
